import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Typography,
    IconButton,
} from '@material-ui/core';
import {
    ArrowBack,
    ArrowDropDown,
    ArrowDropUp,
    ArrowLeft,
    ArrowRight,
    ChevronLeft,
    ChevronRight,
    Close,
    ExpandLessOutlined,
    ExpandMoreOutlined,
    Pause,
    PlayArrow,
    Settings,
    Stop
} from '@material-ui/icons';
import {Box, Button, useMediaQuery, Popover, Slider, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch} from "@mui/material";
import useStyles from '../../../assets/styles/ProgramPreviewStyles';
import {DQC_COLORS, PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT_VERBOSE} from "../../../utils/constants";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {
    getInsiderProgramService,
    getLearningProgramPreviewService,
    getLearningProgramSlidePreviewService,
    getLearningProgramStatusService,
    listV3Level7DQCodeService
} from "../../../services/learning_programs.service";
import {toastWarning} from "../../../utils/utils";
import CancelIcon from '@material-ui/icons/Cancel';
import SlideComponentV2 from "./components/SlideComponentV2.comp";
import {AirbnbSlider, RazThumbComponent} from '../components/ProgressSlider.comp'


function useInterval(callback, delay) {
    const savedCallback = React.useRef();

    React.useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    React.useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

const DQProProgPreview = () => {
    const classes = useStyles();
    const {code: dq_module_code} = useParams();
    const location = useLocation();
    const history = useHistory();
    const [programData, setProgramData] = useState(location.state?.assessment || null);
    const [modulesData, setModulesData] = useState([]);
    const [isContentLoaded, setIsContentLoaded] = useState(false);
    const [openModules, setOpenModules] = useState({});
    const [openUnits, setOpenUnits] = useState({});
    const [openSessions, setOpenSessions] = useState({});
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedSection, setSelectedSection] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [sessionExists, setSessionExists] = useState(true);
    const [currentSegment, setCurrentSegment] = useState(0);
    const [contentStatus, setContentStatus] = useState({});
    const [sessionSlides, setSessionSlides] = useState({});
    const [activeSegment, setActiveSegment] = useState(null);
    const [currentSegmentSlides, setCurrentSegmentSlides] = useState([]);
    const [slidesLoading, setSlidesLoading] = useState(false);
    const [segments, setSegments] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [contentReady, setContentReady] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [allSessionsDone, setAllSessionsDone] = useState(false);
    const [statusPolling, setStatusPolling] = useState(false);
    const [dqcoList, setDqcoList] = useState(location.state?.dqcoList || []);
    const [popupContent, setPopupContent] = useState(null);
    const [ttsAuto, setTtsAuto] = useState(false);
    // STATES FOR NATIVE TTS
    const [speaking, setSpeaking] = useState(false);
    const [paused, setPaused] = useState(false);
    const [currentWordIndex, setCurrentWordIndex] = useState(-1);
    const utteranceRef = React.useRef(null);
    const speechWordsRef = React.useRef([]); // array of words (strings)
    const speechPartRangesRef = React.useRef({}); // id -> { start, end }
    const lastSpokenSlideTitleRef = React.useRef(null);
    const isMobile = useMediaQuery("(max-width:780px)");

    // TTS SETTINGS STATE
    const [ttsRate, setTtsRate] = useState(1);
    const [ttsPitch, setTtsPitch] = useState(1);
    const [ttsVolume, setTtsVolume] = useState(1);
    const [availableVoices, setAvailableVoices] = useState([]);
    const [selectedVoiceURI, setSelectedVoiceURI] = useState('');
    const [ttsSettingsAnchor, setTtsSettingsAnchor] = useState(null);
    
    const TTS_TOAST_WARNING = 'Text to speech seem to have an issue with your browser. Please upgrade the browser version or try another browser.'

    // Load available voices (and refresh when they become available)
    useEffect(() => {
        const loadVoices = () => {
            try {
                const vs = window.speechSynthesis?.getVoices?.() || [];
                const filteredVoices = vs.filter(voice =>
                    voice.lang === 'en-US' || voice.lang === 'en-GB'
                );
                setAvailableVoices(filteredVoices);
            } catch (e) {
                setAvailableVoices([]);
            }
        };
        loadVoices();
        if (window && window.speechSynthesis) {
            window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
        }
        return () => {
            if (window && window.speechSynthesis) {
                window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
            }
        };
    }, []);

    const currentSessionSlide = useMemo(() => (
        currentSegmentSlides && currentSegmentSlides.length > 0
            ? currentSegmentSlides[currentSlide]
            : null
    ), [currentSegmentSlides, currentSlide]);

    // Determine if we should skip title speaking (consecutive same titles)
    const skipTitleSpeech = useMemo(() => {
        if (!currentSegmentSlides || currentSegmentSlides.length === 0) return false;
        const slide = currentSegmentSlides[currentSlide];
        console.log('currentSlide:', currentSlide, 'slide:', slide);
        const title = slide?.SlideTitle || '';
        const prevSlide = currentSlide > 0 ? currentSegmentSlides[currentSlide - 1] : null;
        console.log('skipTitleSpeech check:', {title, prevTitle: prevSlide?.SlideTitle});
        return prevSlide ? prevSlide.SlideTitle === title : lastSpokenSlideTitleRef.current === title;
    }, [currentSegmentSlides, currentSlide]);

    // BUILD SPEECH PARTS AND WORD MAPPING
    const prepareSpeechForSlide = useCallback((slide) => {
        speechWordsRef.current = [];
        speechPartRangesRef.current = {};
        if (!slide) return '';
        const parts = [];
        if (slide.SlideTitle && !skipTitleSpeech) {
            // Add pause after title
            parts.push({id: 'title', text: String(slide.SlideTitle) + '.'});
        }
        const pushContentString = (text, idBase, isSubHeading = false) => {
            if (typeof text === 'string' && text.trim().length) {
                // Add pause after sub-headings (titles in step blocks)
                const processedText = isSubHeading ? text + '.' : text;
                parts.push({id: idBase, text: processedText});
            }
        };
        const sc = slide.SlideContent;
        if (typeof sc === 'string') {
            pushContentString(sc, 'content_0');
        } else if (Array.isArray(sc)) {
            sc.forEach((item, idx) => {
                if (typeof item === 'string') {
                    pushContentString(item, `content_${idx}`);
                } else if (item && typeof item === 'object') {
                    // Step blocks or objects with Title/Text
                    if (item.Title) pushContentString(item.Title, `content_${idx}_title`, true); // Mark as sub-heading
                    if (item.Text) pushContentString(item.Text, `content_${idx}_text`);
                }
            });
        } else if (sc && typeof sc === 'object') {
            Object.entries(sc).forEach(([k, v]) => {
                if (typeof v === 'string') pushContentString(v, `content_obj_${k}`);
            });
        }
        // Build combined text & ranges
        let globalWordIndex = 0;
        parts.forEach(p => {
            const words = p.text.match(/[^\s]+/g) || [];
            const start = globalWordIndex;
            const end = start + words.length - 1;
            speechPartRangesRef.current[p.id] = {start, end};
            speechWordsRef.current.push(...words);
            globalWordIndex = end + 1;
        });
        // Join parts with additional pause markers for better speech flow
        return parts.map(p => p.text).join('. ');
    }, [skipTitleSpeech]);

    const speechText = useMemo(() => prepareSpeechForSlide(currentSessionSlide), [currentSessionSlide, prepareSpeechForSlide]);

    // CLEAN UP ON UNMOUNT
    useEffect(() => {
        return () => {
            window.speechSynthesis.cancel();
        };
    }, []);

    const startSpeech = useCallback(() => {
        if (!speechText) return;

        // Check if speech synthesis is supported
        if (!('speechSynthesis' in window) || !window.speechSynthesis) {
            toastWarning(TTS_TOAST_WARNING);
            return;
        }

        // Cancel any existing speech
        window.speechSynthesis.cancel();

        const utter = new SpeechSynthesisUtterance(speechText);
        utter.lang = 'en-US';
        utter.rate = ttsRate;
        utter.pitch = ttsPitch;
        utter.volume = ttsVolume;

        // Set selected voice if chosen
        const allVoices = window.speechSynthesis.getVoices();
        // Filter to only include en-US and en-GB voices (consistent with availableVoices filtering)
        const voices = allVoices.filter(voice =>
            voice.lang === 'en-US' || voice.lang === 'en-GB'
        );
        console.log('Available voices:', voices.map(v => ({name: v.name, lang: v.lang, gender: v.name.toLowerCase()})));

        // // Additional check: if no voices are available, it might indicate TTS issues
        // if (voices.length === 0) {
        //     // Wait a bit and check again, sometimes voices load asynchronously
        //     setTimeout(() => {
        //         const retryVoices = window.speechSynthesis.getVoices();
        //         if (retryVoices.length === 0) {
        //             toastWarning(TTS_TOAST_WARNING);
        //             return;
        //         }
        //         // If voices are now available, restart the function
        //         startSpeech();
        //     }, 500);
        //     return;
        // }

        if (voices.length > 0) {
            let chosen = null;
            if (selectedVoiceURI) {
                chosen = voices.find(v => v.voiceURI === selectedVoiceURI) || null;
            }
            if (!chosen) {
                // Try to find a female voice with improved detection
                const femaleVoice = voices.find(voice => {
                    const name = voice.name.toLowerCase();
                    const lang = voice.lang.toLowerCase();

                    // Only consider English voices
                    if (!lang.startsWith('en')) return false;

                    return (
                        // Explicit female indicators
                        name.includes('female') ||
                        // Common female voice names across platforms
                        name.includes('zira') ||      // Windows
                        name.includes('susan') ||     // Windows
                        name.includes('samantha') ||  // macOS
                        name.includes('karen') ||     // macOS
                        name.includes('anna') ||      // Various
                        name.includes('marie') ||     // Various
                        name.includes('serena') ||    // Various
                        name.includes('alexia') ||    // Various
                        name.includes('tessa') ||     // Various
                        name.includes('alice') ||     // Chrome/Android
                        name.includes('victoria') ||  // Various
                        name.includes('celia') ||     // macOS Spanish but sometimes available
                        name.includes('amelie') ||    // Various
                        name.includes('ellen') ||     // Various
                        // Default system female voices
                        (name.includes('english') && (name.includes('female') || name.includes('united states') || name.includes('us')))
                    );
                });
                chosen = femaleVoice || null;
                if (!chosen) {
                    // Fallback: try to get any English voice and prefer the second one if available (often female on Windows)
                    const englishVoices = voices.filter(voice => voice.lang.startsWith('en'));
                    console.log('English voices available:', englishVoices.map(v => v.name));

                    if (englishVoices.length >= 2) {
                        console.log('Using second English voice as fallback:', englishVoices[1].name);
                        chosen = englishVoices[1];
                    } else if (englishVoices.length > 0) {
                        console.log('Using first English voice as fallback:', englishVoices[0].name);
                        chosen = englishVoices[0];
                    }
                }
            }
            if (chosen) {
                utter.voice = chosen;
            } else {
                console.warn('No voices available yet, using default voice');
            }
        } else {
            console.warn('No voices available yet, using default voice');
        }

        setCurrentWordIndex(-1);
        speechWordsRef.current = speechText.match(/[^\s]+/g) || [];

        // Set up event handlers before starting speech
        utter.onstart = () => {
            setSpeaking(true);
            setPaused(false);
        };

        utter.onboundary = (e) => {
            if (e.name === 'word' || e.charIndex != null) {
                // Approximate word index by counting words before charIndex
                if (e.charIndex != null) {
                    let count = 0;
                    const textUpToChar = speechText.slice(0, e.charIndex);
                    const arr = textUpToChar.match(/[^\s]+/g);
                    if (arr) count = arr.length;
                    setCurrentWordIndex(count);
                }
            }
        };

        utter.onend = () => {
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
            if (!skipTitleSpeech && currentSessionSlide?.SlideTitle) {
                lastSpokenSlideTitleRef.current = currentSessionSlide.SlideTitle;
            }
        };

        utter.onerror = (event) => {
            console.error('Speech synthesis error:', event);
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
            // // Show toast warning when speech synthesis fails
            // if(event.error === 'canceled') {
            //     toastWarning(TTS_TOAST_WARNING);
            // }
        };

        utteranceRef.current = utter;

        // Try to start speech synthesis
        try {
            window.speechSynthesis.speak(utter);

            // Set a timeout to check if speech actually started
            setTimeout(() => {
                if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
                    // Speech didn't start, reset states
                    setSpeaking(false);
                    setPaused(false);
                    setCurrentWordIndex(-1);
                    console.warn('Speech synthesis failed to start');
                }
            }, 250);

        } catch (error) {
            console.error('Failed to start speech synthesis:', error);
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
            // toastWarning(TTS_TOAST_WARNING);
        }
    }, [speechText, currentSessionSlide, skipTitleSpeech, ttsRate, ttsPitch, ttsVolume, selectedVoiceURI]);

    const pauseSpeech = useCallback(() => {
        if (!speaking) return;
        if (!paused) {
            window.speechSynthesis.pause();
            setPaused(true);
        } else {
            window.speechSynthesis.resume();
            setPaused(false);
        }
    }, [speaking, paused]);

    const stopSpeech = useCallback(() => {
        window.speechSynthesis.cancel();
        setSpeaking(false);
        setPaused(false);
        setCurrentWordIndex(-1);
    }, []);

    // HANDLE AUTO PLAY
    useEffect(() => {
        if (ttsAuto) {
            stopSpeech();
            if (speechText) {
                startSpeech();
            }
        } else {
            stopSpeech();
        }
    }, [ttsAuto, currentSessionSlide, speechText, startSpeech, stopSpeech]);

    // If settings change while speaking with autoplay, restart speech
    useEffect(() => {
        if (ttsAuto && speaking) {
            stopSpeech();
            startSpeech();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ttsRate, ttsPitch, ttsVolume, selectedVoiceURI]);

    // HIGHLIGHT HELPERS PASSED TO SLIDE COMPONENT
    const highlightWordRenderer = useCallback((id, rawText) => {
        if (rawText == null) return null;
        // Normalize rawText to string
        let normalized = rawText;
        if (Array.isArray(normalized)) {
            normalized = normalized
                .map(v => (typeof v === 'string' ? v : (v && typeof v === 'object' ? Object.values(v).filter(x => typeof x === 'string').join(' ') : '')))
                .filter(Boolean)
                .join(' ');
        } else if (typeof normalized === 'object') {
            normalized = Object.values(normalized).filter(v => typeof v === 'string').join(' ');
        }
        if (typeof normalized !== 'string') normalized = String(normalized);
        if (!normalized.trim()) return null;

        const range = speechPartRangesRef.current[id];
        const words = normalized.match(/[^\s]+/g) || [];
        if (!range) {
            return words.map((w, i) => <span key={`${id}-w-${i}`}>{w + ' '}</span>);
        }
        const {start} = range;
        return words.map((w, i) => {
            const globalIndex = start + i;
            const isCurrent = globalIndex === currentWordIndex;
            return <span key={`${id}-w-${i}`} style={isCurrent ? {
                backgroundColor: '#FFF3CD',
                color: '#000',
                borderRadius: '4px'
            } : {}}>{w + ' '}</span>;
        });
    }, [currentWordIndex]);

    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                if (!programData && dq_module_code) {
                    setLoading(true);
                    const response = await getInsiderProgramService(dq_module_code);
                    if (response?.data?.learning_program) {
                        setProgramData(response.data.learning_program);
                        console.log('program: ', response.data.learning_program);
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData().then(r => console.log(r));
    }, [programData, dq_module_code]);

    useEffect(() => {
        const fetchModules = async () => {
            if (!dq_module_code) {
                setModulesData([]);
                setLoading(false);
                setInitialLoading(false);
                return;
            }

            try {
                setLoading(true);
                const res = await getLearningProgramPreviewService(dq_module_code);
                if (res?.data?.program_layout_preview) {
                    console.log('outline: ', res.data.program_layout_preview)
                    const program = res?.data?.program_layout_preview || {};
                    if (!program || !Array.isArray(program.modules)) {
                        setModulesData([]);
                        setLoading(false);
                        setInitialLoading(false);
                        return;
                    }

                    const mappedModules = program.modules.map(mod => ({
                        module_code: mod.module_code || '',
                        module_name: mod.module_title || '',
                        module_description: mod.description_for_educator || '',
                        module_duration: mod.total_duration_minutes || '',
                        module_items_definitions: {
                            title: mod.definition_title || '',
                            description: mod.definition || ''
                        },
                        unique_dqco: mod.unique_dqco || [],
                        units: (mod.units || []).map(unit => ({
                            unit_code: unit.unit_code || '',
                            unit_title: unit.unit_title || '',
                            unit_description: unit.description_for_educator || '',
                            unit_duration: unit.total_duration_minutes || 0,
                            lessons: (unit.sessions || []).map(session => ({
                                session_code: session.session_code || '',
                                lesson_title: session.session_title || '',
                                lesson_duration: session.duration_minutes || 0,
                                lesson_description: session.description_for_educator || '',
                                lesson_tokens: session.lesson_tokens || [],
                                session_learning_objectives: session.session_learning_objectives || [],
                            })),
                        })),
                    }));

                    setModulesData(mappedModules);

                    // Auto-open first module and fetch first session content
                    if (mappedModules.length > 0) {
                        setOpenModules(prev => ({...prev, 0: true}));

                        if (mappedModules[0].units.length > 0) {
                            setOpenUnits(prev => ({...prev, '0-0': true}));

                            if (mappedModules[0].units[0].lessons.length > 0) {
                                const firstSession = mappedModules[0].units[0].lessons[0];
                                setTimeout(() => {
                                    fetchFirstSessionContent(firstSession);
                                }, 500);
                            } else {
                                setInitialLoading(false);
                            }
                        } else {
                            setInitialLoading(false);
                        }
                    } else {
                        setInitialLoading(false);
                    }
                }
            } catch (err) {
                console.error('Error fetching modules:', err);
                setModulesData([]);
                setInitialLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchModules().then(r => console.log(r));
    }, [dq_module_code]);

    const getSloTitlesForSession = (session) => {
        // Handles both old and new data shape, expects array of SLOs
        if (!session?.session_learning_objectives) return {};
        const sloTitles = {};
        session.session_learning_objectives.forEach((slo, idx) => {
            sloTitles[`SLO${idx + 1}`] = slo.slo_title || `SLO${idx + 1}`;
        });
        return sloTitles;
    };


    const getSegmentDisplayName = (segmentKey, session) => {
        if (segmentKey.startsWith("SLO")) {
            // Dynamically fetch SLO title from session object
            const sloTitles = getSloTitlesForSession(session);
            return sloTitles[segmentKey] || segmentKey;
        }

        const staticNames = {
            "Seg1": "Introduction",
            "Seg4": "Reflection",
            "Seg5": "Recap"
        };
        return staticNames[segmentKey] || segmentKey;
    };

    const fetchFirstSessionContent = useCallback(async (session) => {
        if (!session || !programData?.target_audience) {
            setInitialLoading(false);
            return;
        }

        // Check if session is ready
        const sessionStatus = contentStatus[session.session_code];
        if (sessionStatus && sessionStatus !== 'done') {
            // Show toast for non-ready session
            toastWarning(`Session "${session.lesson_title}" is ${sessionStatus === 'in_progress' ? 'still generating' : 'not available'}.`);
            setInitialLoading(false);
            return;
        }

        setSlidesLoading(true);
        try {
            const result = await getLearningProgramSlidePreviewService(
                programData.code,
                session.session_code
            );

            if (result?.data?.slide_preview) {
                const slideData = result.data.slide_preview;
                // remove keys CourseCode & session_code from slideData
                console.log('First slide data:', slideData);

                setSessionSlides(slideData);
                setOpenSessions(prev => ({...prev, [session.session_code]: true}));

                // Extract segments from the data
                const segmentsList = Object.keys(slideData).map(segmentKey => ({
                    id: segmentKey,
                    name: getSegmentDisplayName(segmentKey, session)
                }));

                setSegments(segmentsList);

                if (segmentsList.length > 0) {
                    setSelectedSession(session);
                    setSelectedSection('Session Details');
                    setActiveSegment(segmentsList[0].id);
                    setCurrentSegment(0);
                }
            }
        } catch (err) {
            console.error("Error fetching first session slides:", err);
        } finally {
            setSlidesLoading(false);
            setInitialLoading(false);
        }
    }, [programData]);

    useEffect(() => {
        // Only run if programData and modulesData are loaded
        if (programData?.target_audience && modulesData.length > 0 &&
            modulesData[0].units?.length > 0 &&
            modulesData[0].units[0].lessons?.length > 0) {
            const firstSession = modulesData[0].units[0].lessons[0];
            fetchFirstSessionContent(firstSession);
        }
    }, [programData, modulesData, fetchFirstSessionContent]);

    const fetchContentStatus = useCallback(async () => {
        try {
            const res = await getLearningProgramStatusService(dq_module_code);
            if (res?.data?.contentStatus) {
                const statuses = res.data.contentStatus;
                setContentStatus(statuses);
                console.log('contentStatus: ', statuses);

                // Check if all sessions are done
                // Check if any sessions are still in progress
                const anyInProgress = Object.values(statuses).some(status => status === 'in_progress');
                setAllSessionsDone(!anyInProgress);

                // Stop polling if none are in progress
                if (!anyInProgress && statusPolling) {
                    console.log('No sessions in progress, stopping polling');
                    setStatusPolling(false);
                }
            }
        } catch (err) {
            console.error('Error fetching content status:', err);
            setContentStatus({});
        }
    }, [dq_module_code, statusPolling]);

    // Initial content status fetch and start polling
    useEffect(() => {
        if (dq_module_code) {
            fetchContentStatus();
            // Start polling if not already polling and not all sessions are done
            if (!statusPolling && !allSessionsDone) {
                setStatusPolling(true);
            }
        }
    }, [dq_module_code, fetchContentStatus]);

    // Polling interval for status updates
    useInterval(() => {
        if (statusPolling) {
            fetchContentStatus();
        }
    }, statusPolling ? 5000 : null);

    useEffect(() => {
        if (activeSegment && sessionSlides[activeSegment]) {
            // Ensure this is an array of slide objects
            const slides = Array.isArray(sessionSlides[activeSegment])
                ? sessionSlides[activeSegment]
                : [sessionSlides[activeSegment]];

            setCurrentSegmentSlides(slides);
            setCurrentSlide(0);
        } else {
            setCurrentSegmentSlides([]);
        }
    }, [activeSegment, sessionSlides]);

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleToggleModuleDropdown = (moduleIndex) => {
        setOpenModules(prev => ({
            ...prev,
            [moduleIndex]: !prev[moduleIndex]
        }));
    };

    const handleToggleUnitDropdown = (moduleIndex, unitIndex) => {
        setOpenUnits(prev => ({
            ...prev,
            [`${moduleIndex}-${unitIndex}`]: !prev[`${moduleIndex}-${unitIndex}`]
        }));
    };

    const handleToggleSessionDropdown = (session) => {
        const sessionCode = session.session_code;
        const sessionStatus = contentStatus[sessionCode];

        // Don't allow toggling for failed/halted sessions
        if (sessionStatus === 'failed') {
            toastWarning(`Session "${session.lesson_title}" is not available.`);
            return;
        }

        // For in_progress sessions, show notification
        if (sessionStatus === 'in_progress') {
            toastWarning(`Session "${session.lesson_title}" is still generating. Please wait.`);
            return;
        }

        // For done sessions, proceed normally
        // Close other sessions if this one is being opened
        if (!openSessions[sessionCode]) {
            // Create a new object with all sessions closed except the current one
            const newOpenSessions = {};
            Object.keys(openSessions).forEach(key => {
                newOpenSessions[key] = false;
            });
            newOpenSessions[sessionCode] = true;
            setOpenSessions(newOpenSessions);

            // Clear current segments when switching sessions
            if (selectedSession?.session_code !== sessionCode) {
                setSegments([]);
                setCurrentSegmentSlides([]);
                setActiveSegment(null);
            }

            // Fetch the slides for this session
            fetchSessionSlides(session);
        } else {
            // Toggle the current session closed if it was open
            setOpenSessions(prev => ({
                ...prev,
                [sessionCode]: !prev[sessionCode]
            }));
        }
    };

    const fetchSessionSlides = async (session) => {
        if (!session) return;

        setSlidesLoading(true);
        setSelectedSession(session); // Set the selected session immediately

        try {
            const result = await getLearningProgramSlidePreviewService(
                programData?.code,
                session.session_code
            );

            if (result?.data?.slide_preview) {
                const slideData = result.data.slide_preview;
                // remove keys CourseCode & session_code from slideData
                console.log('Slide data:', slideData);

                // Store the raw slide data
                setSessionSlides(slideData);

                // Extract segments from the data
                const segmentsList = Object.keys(slideData).map(segmentKey => ({
                    id: segmentKey,
                    name: getSegmentDisplayName(segmentKey, session)
                }));

                setSegments(segmentsList);

                // Automatically select the first segment
                if (segmentsList.length > 0) {
                    setActiveSegment(segmentsList[0].id);
                    setCurrentSlide(0);
                    setSelectedSection('Session Details');
                    setCurrentSegment(0);
                }
            } else {
                setSessionSlides({});
                setSegments([]);
            }
        } catch (err) {
            setSessionSlides({});
            setSegments([]);
            console.error("Error fetching slides:", err);
        } finally {
            setSlidesLoading(false);
        }
    };

    const handleViewSegment = (session, segmentId) => {
        setSelectedSession(session);
        setSelectedSection('Session Details');
        setActiveSegment(segmentId);
        setCurrentSlide(0);

        // Find the index of the selected segment
        const segmentIndex = segments.findIndex(seg => seg.id === segmentId);
        if (segmentIndex !== -1) {
            setCurrentSegment(segmentIndex);
        }
    };

    const handleNextSlide = () => {
        if (currentSlide < currentSegmentSlides.length - 1) {
            setCurrentSlide(prev => prev + 1);
        } else if (currentSegment < segments.length - 1) {
            // Move to the next segment if at the end of current segment slides
            handleNextSegment();
        }
    };

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        } else if (currentSegment > 0) {
            // Move to the previous segment if at the beginning of current segment slides
            handlePreviousSegment();
        }
    };

    const handlePreviousSegment = () => {
        if (segments.length === 0) return;

        const currentIndex = segments.findIndex(seg => seg.id === activeSegment);
        if (currentIndex > 0) {
            const prevSegment = segments[currentIndex - 1];
            setActiveSegment(prevSegment.id);
            setCurrentSlide(sessionSlides[prevSegment.id]?.length - 1 || 0); // Go to last slide of previous segment
            setCurrentSegment(currentIndex - 1);
        }
    };

    const handleNextSegment = () => {
        if (segments.length === 0) return;

        const currentIndex = segments.findIndex(seg => seg.id === activeSegment);
        if (currentIndex < segments.length - 1) {
            const nextSegment = segments[currentIndex + 1];
            setActiveSegment(nextSegment.id);
            setCurrentSlide(0); // Go to first slide of next segment
            setCurrentSegment(currentIndex + 1);
        }
    };

    useEffect(() => {
        if (dqcoList.length === 0) fetchV3Level7DQCodeList();
        // eslint-disable-next-line
    }, []);

    const fetchV3Level7DQCodeList = async () => {
        try {
            const res = await listV3Level7DQCodeService();
            if (res?.data?.metaData) {
                setDqcoList(res.data.metaData);
            }
        } catch (err) {
            // Optionally handle error
            setDqcoList([]);
        }
    };

    const handleModuleCodeClick = async (module, dqco) => {
        // If dqcoList is empty, fetch it
        if (dqcoList.length === 0) {
            await fetchV3Level7DQCodeList();
        }

        const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);

        setPopupContent({
            code: dqco,
            dqc_no: dqcoObj?.dqc_no,
            title: dqcoObj?.full_name,
            description: dqcoObj?.definition
        });
    };

    const handleLaunchProgram = () => {
        setDialogOpen(false);
        if (segments.length > 0) {
            setActiveSegment(segments[0].id);
            setCurrentSegment(0);
            setCurrentSlide(0);
        }
        console.log('Program launched!');
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const isSegmentActive = (segmentId) => {
        return activeSegment === segmentId;
    };

    // Determine if we should show next/previous segment buttons on slides
    const showNextSegmentButton = currentSlide === currentSegmentSlides.length - 1 && currentSegment < segments.length - 1;
    const showPrevSegmentButton = currentSlide === 0 && currentSegment > 0;

    return (
        <Box sx={{
            mt:{xs:2,sm:'75px', lg:2},
            overflowY: 'hidden',
            flexDirection: {xs: 'column', sm: 'row'},
            display: 'flex',
            width: '100%',
            minHeight: '82vh'
        }}>
            {/* Left Container - Curriculum List */}
            <Box className={classes.leftContainer} style={{
                height: isMobile ? (isCollapsed ? "100px" : "82vh") : "82vh",
                width: !isMobile ? (isCollapsed ? "48px" : "32%") : "100%",
                marginRight: !isMobile ? (isCollapsed ? "20px" : "20px") : "auto",
                padding: !isMobile ? (isCollapsed ? "6px 4px" : "10px") : "auto",
                borderRadius: isCollapsed ? "8px" : "8px",
                alignItems: isCollapsed && !isMobile ? 'center' : undefined
            }}>
                {isCollapsed ? (
                    isMobile ? (
                        <Box
                            style={{
                                width: "100%",
                                padding: "8px 0",
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <IconButton
                                onClick={() => history.push(`/dq_pro/products/program/dashboard/${dq_module_code}`)}
                                disableRipple
                                disableFocusRipple
                                style={{
                                    color: '#0097B2',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e6e9ef',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                    cursor: 'pointer',
                                    outline: "none",
                                }}
                            >
                                <ArrowBack style={{fontSize: 22}}/>
                            </IconButton>

                            {/* Title on top (horizontal) */}
                            <Typography variant="h4" className={classes.title} style={{textAlign: "center",}}>
                                {programData?.full_name}
                            </Typography>

                            {/* Expand button below title */}
                            <IconButton
                                onClick={toggleCollapse}
                                style={{
                                    marginTop: "6px",
                                    color: "#ef6528",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e6e9ef",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                }}
                            >
                                <ExpandMoreOutlined style={{fontSize: 22}}/>
                            </IconButton>
                        </Box>
                    ) : (
                        <>
                            {/* Expand button at top */}
                            <IconButton
                                onClick={toggleCollapse}
                                style={{
                                    position: "absolute",
                                    top: 10,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    color: "#ef6528",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e6e9ef",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                                    zIndex: 2,
                                    padding: 6,
                                }}
                            >
                                <ChevronRight style={{fontSize: 22}}/>
                            </IconButton>

                            {/* Vertical program title in center */}
                            <Box
                                style={{
                                    flexGrow: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "bold",
                                    color: "#0097B2",
                                    padding: "0 10px",
                                    cursor: "default"
                                }}
                            >
                                <Typography variant="h4" className={classes.title} style={{
                                    writingMode: "vertical-rl",
                                    transform: "rotate(180deg)",
                                    textAlign: "center",
                                }}>
                                    {programData?.full_name}
                                </Typography>
                            </Box>
                        </>
                    )
                ) : (
                    <Box className={classes.leftContentContainer}>
                        {loading ? (
                            <Typography>Loading...</Typography>
                        ) : (
                            <>
                                {/*icon button to go back to program dashboard page*/}
                                {!isCollapsed && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '75px',
                                        mb: 4,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0 8px',
                                        backgroundColor: '#ffff',
                                        zIndex: 100,
                                    }}>
                                        <IconButton
                                            onClick={() => history.push(`/dq_pro/products/program/dashboard/${dq_module_code}`)}
                                            disableRipple
                                            disableFocusRipple
                                            style={{
                                                color: '#0097B2',
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e6e9ef',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                                cursor: 'pointer',
                                                outline: "none",
                                            }}
                                        >
                                            <ArrowBack style={{fontSize: 22}}/>
                                        </IconButton>

                                        <Typography variant="h4" className={classes.title}>
                                            {programData?.full_name}
                                        </Typography>
                                        {/*icon button to collapse course catalogue*/}
                                        <IconButton
                                            onClick={toggleCollapse}
                                            disableRipple
                                            disableFocusRipple
                                            style={{
                                                transition: 'all 0.3s ease',
                                                zIndex: 99,
                                                cursor: 'pointer',
                                                outline: "none",
                                                color: '#ef6528',
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e6e9ef',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                                            }}
                                        >
                                            {isMobile ? (
                                                <ExpandLessOutlined style={{fontSize: 22}}/>
                                            ) : (
                                                <ChevronLeft style={{fontSize: 22}}/>
                                            )}
                                        </IconButton>
                                    </Box>
                                )}

                                {modulesData.map((module, moduleIndex) => (
                                    <Card className={classes.card} key={module.module_code}>
                                        <CardContent className={classes.cardContent}>
                                            <Box
                                                onClick={() => handleToggleModuleDropdown(moduleIndex)}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="h6" style={{
                                                        fontSize: '1.2rem',
                                                        color: '#ED6628',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}>
                                                        {`Module ${moduleIndex + 1}: ${module.module_name}`}
                                                    </Typography>
                                                    {openModules[moduleIndex] ? (
                                                        <ArrowDropUp style={{color: '#D35400'}}/>
                                                    ) : (
                                                        <ArrowDropDown style={{color: '#D35400'}}/>
                                                    )}
                                                </Box>

                                                <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                                                    {module.unique_dqco?.map((dqco, idx) => {
                                                        const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);
                                                        return (
                                                            <span
                                                                key={dqco || idx}
                                                                style={{
                                                                    backgroundColor: DQC_COLORS[dqcoObj?.dqc_no] || '#D2DD2C',
                                                                    color: '#ffffff',
                                                                    borderRadius: '20px',
                                                                    fontSize: '12px',
                                                                    padding: '1px 7px',
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer',
                                                                    margin: '4px',
                                                                }}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    handleModuleCodeClick(module, dqco);
                                                                }}
                                                            >
                                                        {dqco}
                                                    </span>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                            {popupContent && (
                                                <Box
                                                    style={{
                                                        position: 'fixed',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '80%',
                                                        maxWidth: '500px',
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #000',
                                                        borderRadius: '20px',
                                                        boxShadow: 24,
                                                        padding: 4,
                                                        zIndex: 1300,
                                                    }}
                                                    onClick={() => setPopupContent(null)}
                                                >
                                                    <IconButton
                                                        aria-label="close"
                                                        onClick={() => setPopupContent(null)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 0,
                                                            color: '#000',
                                                        }}
                                                    >
                                                        <Close/>
                                                    </IconButton>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 2, padding: '15px 20px',
                                                    }}>
                                                        <Box sx={{
                                                            backgroundColor: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                            color: 'white',
                                                            borderRadius: '20px',
                                                            px: 2,
                                                            py: 1,
                                                            fontWeight: 'medium',
                                                            mb: 2
                                                        }}>
                                                            {popupContent.code}
                                                        </Box>

                                                        {/* Title */}
                                                        <Typography variant="h5" component="h2" style={{
                                                            fontWeight: 'bold',
                                                            textAlign: 'center',
                                                            color: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                        }}>
                                                            {popupContent.title}
                                                        </Typography>

                                                        {/* Description */}
                                                        <Typography style={{
                                                            mt: 2,
                                                            whiteSpace: 'pre-line',
                                                            textAlign: 'left',
                                                            fontSize: '11px',
                                                            color: '#000'
                                                        }}>
                                                            {popupContent.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            {openModules[moduleIndex] && (
                                                <>
                                                    {module.units.map((unit, unitIndex) => (
                                                        <Card
                                                            style={{boxShadow: 'none', backgroundColor: 'transparent'}}
                                                            key={unit.unit_code}>
                                                            <CardContent>
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    justifyContent="space-between"
                                                                    onClick={() => handleToggleUnitDropdown(moduleIndex, unitIndex)}
                                                                    style={{cursor: 'pointer'}}
                                                                >
                                                                    <Typography variant="h6" style={{color: '#e7456d'}}>
                                                                        {`Unit ${unitIndex + 1}: ${unit.unit_title}`}
                                                                    </Typography>
                                                                    {openUnits[`${moduleIndex}-${unitIndex}`] ? (
                                                                        <ArrowDropUp style={{color: '#ED6628'}}/>
                                                                    ) : (
                                                                        <ArrowDropDown style={{color: '#ED6628'}}/>
                                                                    )}
                                                                </Box>

                                                                {openUnits[`${moduleIndex}-${unitIndex}`] && (
                                                                    <>
                                                                        <Divider style={{
                                                                            height: '4px',
                                                                            marginTop: '10px',
                                                                            backgroundColor: '#F17632',
                                                                            borderRadius: '15px',
                                                                        }}/>
                                                                        {unit.lessons.map((session, sessionIndex) => (
                                                                            <Box key={session.session_code}>
                                                                                <Box
                                                                                    className={classes.lessonContainer}
                                                                                    style={{
                                                                                        backgroundColor: selectedSession?.session_code === session.session_code
                                                                                            ? 'rgba(238, 102, 40, 0.15)'
                                                                                            : 'transparent',
                                                                                        borderRadius: selectedSession?.session_code === session.session_code
                                                                                            ? '8px'
                                                                                            : '0px',
                                                                                        cursor: contentStatus[session.session_code] === 'done' ? 'pointer' : 'default',
                                                                                        opacity: contentStatus[session.session_code] === 'failed' ? 0.6 : 1
                                                                                    }}
                                                                                    onClick={() => contentStatus[session.session_code] === 'done' && handleToggleSessionDropdown(session)}
                                                                                >
                                                                                    <Box className={classes.sectionRow}>
                                                                                        <Typography
                                                                                            variant="h4"
                                                                                            className={classes.sectionText}
                                                                                            style={{
                                                                                                color: contentStatus[session.session_code] === 'failed' ? '#999' : '#5A5A5A'
                                                                                            }}
                                                                                        >
                                                                                            {`Session ${sessionIndex + 1}: ${session.lesson_title}`}
                                                                                        </Typography>

                                                                                        {/* Status indicators based on contentStatus */}
                                                                                        {contentStatus[session.session_code] === 'in_progress' ? (
                                                                                            <CircularProgress size={20}
                                                                                                              style={{color: '#ED6628'}}/>
                                                                                        ) : contentStatus[session.session_code] === 'failed' ? (
                                                                                            <CancelIcon
                                                                                                style={{color: '#FF5757'}}/>
                                                                                        ) : openSessions[session.session_code] ? (
                                                                                            <ArrowDropUp
                                                                                                style={{color: '#ED6628'}}/>
                                                                                        ) : (
                                                                                            <ArrowDropDown
                                                                                                style={{color: '#ED6628'}}/>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>

                                                                                {/* Session segments in the side menu */}
                                                                                {openSessions[session.session_code] && (
                                                                                    <Box style={{marginTop: '8px'}}>
                                                                                        {/* Show loader while segments are loading */}
                                                                                        {slidesLoading && selectedSession?.session_code === session.session_code ? (
                                                                                            <Box style={{
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                justifyContent: 'center',
                                                                                                padding: '15px'
                                                                                            }}>
                                                                                                <CircularProgress
                                                                                                    size={20}
                                                                                                    style={{color: '#ED6628'}}/>
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    style={{
                                                                                                        marginLeft: '10px',
                                                                                                        color: '#555'
                                                                                                    }}>
                                                                                                    Loading segments...
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        ) : (
                                                                                            segments.map((segment, segIdx) => {
                                                                                                const isActive = isSegmentActive(segment.id) && selectedSession?.session_code === session.session_code;

                                                                                                return (
                                                                                                    <Box
                                                                                                        className={classes.lessonContainer}
                                                                                                        key={segment.id || segIdx}
                                                                                                        style={{
                                                                                                            backgroundColor: isActive ? 'rgba(238, 102, 40, 0.1)' : 'transparent',
                                                                                                            borderRadius: isActive ? '8px' : '0',
                                                                                                            padding: '8px 10px 8px 20px',
                                                                                                            marginBottom: '4px'
                                                                                                        }}
                                                                                                    >
                                                                                                        <Box
                                                                                                            className={classes.sectionSegmentRow}>
                                                                                                            <Typography
                                                                                                                variant="h6"
                                                                                                                className={classes.sectionText}
                                                                                                                style={{
                                                                                                                    color: isActive ? '#ED6628' : '#000000',
                                                                                                                    fontWeight: isActive ? 600 : 400
                                                                                                                }}
                                                                                                            >
                                                                                                                {segment.name}
                                                                                                            </Typography>
                                                                                                            <Button
                                                                                                                variant="contained"
                                                                                                                className={classes.viewButton}
                                                                                                                disabled={slidesLoading}
                                                                                                                onClick={() => {
                                                                                                                    handleViewSegment(session, segment.id);
                                                                                                                    if (isMobile) {
                                                                                                                        toggleCollapse();
                                                                                                                    }
                                                                                                                }}
                                                                                                                style={{
                                                                                                                    backgroundColor: isActive ? '#ED6628' : '#0097B2',
                                                                                                                    color: '#fff'
                                                                                                                }}
                                                                                                            >
                                                                                                                View
                                                                                                            </Button>
                                                                                                        </Box>
                                                                                                    </Box>
                                                                                                );
                                                                                            })
                                                                                        )}
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                                <IconButton
                                    style={{
                                        display: 'none',
                                        position: 'absolute',
                                        top: '115px',
                                        left: '50%',
                                        fontSize: '30px',
                                        transform: 'translateX(-50%)',
                                        color: '#F17632',
                                    }}
                                >
                                    {isCollapsed ? (
                                        <ArrowDropDown style={{color: '#ED6628'}}/>
                                    ) : (
                                        <ArrowDropUp style={{color: '#ED6628'}}/>
                                    )}
                                </IconButton>
                            </>
                        )}

                    </Box>
                )}
            </Box>

            {/* Right Container - Slideshow or Initial Loading State */}
            {initialLoading ? (
                <Box className={classes.rightContainer} style={{
                    display: 'flex',
                    height: '82vh',
                    overflow: 'scroll',
                    overflowX: 'hidden',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <CircularProgress size={60} style={{color: '#ED6628', marginBottom: '20px'}}/>
                    <Typography variant="h5" style={{color: '#555', textAlign: 'center', maxWidth: '600px'}}>
                        Loading content... This might take a while as we prepare the learning materials.
                    </Typography>
                    <Typography variant="body1"
                                style={{color: '#777', textAlign: 'center', maxWidth: '600px', marginTop: '20px'}}>
                        Please be patient. The first load may take longer than usual, especially if content is being
                        generated.
                    </Typography>
                </Box>
            ) : (selectedSession && selectedSection && activeSegment) ? (
                <Box className={classes.rightContainer}
                     style={{
                         width: !isMobile ? (isCollapsed ? "100%" : "68%") : "100%",
                         maxWidth: '100%',
                         overflowX: 'hidden'
                     }}>
                    {/* Slides Content */}
                    <Box className={classes.rightContentContainer}>
                        <Typography variant="h4" style={{
                            color: '#ED6628',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            marginBottom: '40px !important',
                            marginTop: '10px',
                            fontSize: 'clamp(30px, 3.2vw, 35px)'
                        }}>
                            {selectedSession?.lesson_title}
                        </Typography>
                        <Box style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: isMobile ? '85%' : isCollapsed ? '80%' : '88%',
                            marginTop: '13px',
                            gap: '12px',
                            flexWrap: 'nowrap',
                        }}>
                            <Box style={{flex: '1 1 auto', minWidth: 0, paddingRight: 28}}>
                                <AirbnbSlider
                                    value={currentSlide}
                                    min={0}
                                    max={currentSegmentSlides.length > 0 ? currentSegmentSlides.length - 1 : 0}
                                    step={1}
                                    slots={{thumb: RazThumbComponent}}
                                    disabled
                                    style={{width: '100%', minWidth: 0}}
                                />
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                flex: '0 0 auto',
                                width: 'auto',
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                marginTop: '4px',
                            }}>
                                <Typography style={{
                                    color: '#777',
                                    fontWeight: '700',
                                    fontSize: 'clamp(16px, 2.4vw, 22px)',
                                    paddingLeft: 4
                                }}>
                                    <span
                                        style={{color: '#ED6628'}}>{currentSlide + 1} </span>/ {currentSegmentSlides.length}
                                </Typography>
                            </Box>
                        </Box>

                        <Box className={classes.slidesContainer} sx={{
                            padding: {xs: '0px', sm: '0 20px 20px 20px'},
                            width: {xs: '90vw', sm: '100%'},
                            paddingLeft: {xs: '52px', sm: '72px', md: '0px',},
                            paddingRight: {xs: '52px', sm: '72px', md: '0px',},
                        }}>
                            {slidesLoading ? (
                                <Box style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '300px',
                                    width: '100%'
                                }}>
                                    <CircularProgress size={40} style={{color: '#ED6628', marginBottom: '20px'}}/>
                                    <Typography variant="body1" style={{color: '#555'}}>
                                        Loading slides...
                                    </Typography>
                                </Box>
                            ) : currentSegmentSlides.length > 0 && currentSegmentSlides[currentSlide] ? (() => {
                                const sessionSlide = currentSegmentSlides[currentSlide];
                                const isV2SupportedLayout = [
                                    'TemplateGraphicAndText',
                                    'TemplateList',
                                    'TemplateLongText',
                                    'TemplateStepBlock',
                                    'TemplateSessionStart',
                                    'PledgeTemplate',
                                    'VideoTemplate',
                                    'JOURNALING_SELF_REFLECTION_ACTIVITY',
                                    'SHARE_WORK_FILE_ACTIVITY',
                                    'POLL_QUIZ_ACTIVITY',
                                    'DISCUSSION_ACTIVITY',
                                ].includes(sessionSlide.SlideLayout);

                                if (isV2SupportedLayout) {
                                    return (
                                        <SlideComponentV2
                                            slide={sessionSlide}
                                            highlightWord={highlightWordRenderer}
                                            skipTitleSpeech={skipTitleSpeech}
                                        />
                                    );
                                } else {
                                    const SlideComponent = PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT_VERBOSE[sessionSlide.SlideLayout];

                                    if (!SlideComponent) {
                                        console.error(`No component found for layout: ${sessionSlide.SlideLayout}`);
                                        return (
                                            <Typography variant="h6" color="error">
                                                Error: Slide layout not found
                                            </Typography>
                                        );
                                    }

                                    return (
                                        <SlideComponent
                                            title={sessionSlide.SlideTitle}
                                            slideContent={Array.isArray(sessionSlide.SlideContent) ? sessionSlide.SlideContent : [sessionSlide.SlideContent]}
                                            images={sessionSlide.Images || []}
                                        />
                                    );
                                }
                            })() : (
                                <Typography variant="h6" style={{color: '#555'}}>
                                    No slides available for this segment
                                </Typography>
                            )}
                        </Box>

                        {/* Navigation Buttons Below Slides */}
                        <Box sx={{
                            flex: '0 0 auto',
                            borderTop: '1px solid #eee',
                            display: 'flex',
                            flexDirection: {xs: 'column', lg: 'row'},
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '90%',
                            margin: '20px 0 0 0',
                            padding: '10px 0 0 0',
                            gap: {xs: '20px', sm: '0'},
                        }}>
                            <Button
                                variant="outlined"
                                sx={{
                                    fontSize: {xs: '12px', sm: 'auto',},
                                    border: '1px solid #0097B2',
                                    color: '#0097B2',
                                    backgroundColor: '#fff',
                                    padding: '8px 20px',
                                    borderRadius: '20px',
                                    opacity: currentSegment === 0 ? 0.5 : 1,
                                    minWidth: '220px',
                                }}
                                onClick={handlePreviousSegment}
                                disabled={currentSegment === 0}
                            >
                                View Previous Segment
                            </Button>

                            {/* TTS Controls */}
                            <div style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                marginTop: 12,
                                marginBottom: 12,
                            }}>
                                <IconButton
                                    disabled={!speaking}
                                    style={{
                                        backgroundColor: speaking ? '#e7456d' : '#cccccc',
                                        color: '#fff',
                                        width: '48px',
                                        height: '48px',
                                        opacity: speaking ? 1 : 0.5,
                                        boxShadow: speaking ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                                    }}
                                    onClick={() => {
                                        if (!speaking) {
                                            setTtsAuto(true);
                                            startSpeech();
                                        } else {
                                            setTtsAuto(false);
                                            stopSpeech();
                                        }
                                    }}

                                >
                                    <Stop/>
                                </IconButton>
                                <IconButton
                                    style={{
                                        backgroundColor: paused ? '#ED6628' : speaking ? '#0097B2' : '#ED6628',
                                        color: '#fff',
                                        width: '48px',
                                        height: '48px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    }}
                                    onClick={() => {
                                    if (!speaking) {
                                        setTtsAuto(true);
                                        startSpeech();
                                    } else {
                                        pauseSpeech();
                                    }
                                  }}
                                >
                                    {speaking && !paused ? <Pause /> : <PlayArrow />}
                                </IconButton>

                                {/* Settings toggle */}
                                <IconButton
                                    aria-label="TTS settings"
                                    onClick={(e) => setTtsSettingsAnchor(e.currentTarget)}
                                    style={{
                                        background: '#ff6468',
                                        color: '#fff',
                                        width: '48px',
                                        height: '48px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                    }}
                                >
                                    <Settings/>
                                </IconButton>

                                <Popover
                                    open={Boolean(ttsSettingsAnchor)}
                                    anchorEl={ttsSettingsAnchor}
                                    onClose={() => setTtsSettingsAnchor(null)}
                                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                                    transformOrigin={{vertical: 'top', horizontal: 'center'}}
                                    PaperProps={{
                                        sx: {
                                            p: 2,
                                            borderRadius: 2,
                                            minWidth: 300,
                                            maxWidth: 360,
                                        }
                                    }}
                                >
                                    <Box sx={{position: 'relative'}}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setTtsSettingsAnchor(null)}
                                            style={{position: 'absolute', top: -8, right: -8, background: '#eee'}}
                                        >
                                            <Close fontSize="small"/>
                                        </IconButton>
                                        <Typography variant="h6" style={{textAlign:'center',fontWeight: 700, color: '#ff6468', marginBottom: 12}}>
                                            TTS Settings
                                        </Typography>

                                        <FormControl fullWidth size="small" style={{marginBottom: 12}}>
                                            <InputLabel id="voice-select-label">Voice</InputLabel>
                                            <Select
                                                labelId="voice-select-label"
                                                label="Voice"
                                                value={selectedVoiceURI || ''}
                                                onChange={(e) => setSelectedVoiceURI(e.target.value)}
                                            >
                                                {availableVoices.length === 0 && (
                                                    <MenuItem value="" disabled>No voices available yet</MenuItem>
                                                )}
                                                {availableVoices.map(v => (
                                                    <MenuItem key={v.voiceURI} value={v.voiceURI}>
                                                        {v.name} ({v.lang}){v.default ? ' • default' : ''}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Box style={{marginBottom: 10}}>
                                            <Typography variant="caption"
                                                        style={{color: '#666'}}>Rate: {ttsRate.toFixed(2)}</Typography>
                                            <Slider
                                                size="small"
                                                value={ttsRate}
                                                min={0.5}
                                                max={1.75}
                                                step={0.05}
                                                onChange={(_, v) => setTtsRate(Array.isArray(v) ? v[0] : v)}
                                                valueLabelDisplay="auto"
                                            />
                                        </Box>
                                        <Box style={{marginBottom: 10}}>
                                            <Typography variant="caption"
                                                        style={{color: '#666'}}>Pitch: {ttsPitch.toFixed(2)}</Typography>
                                            <Slider
                                                size="small"
                                                value={ttsPitch}
                                                min={0.5}
                                                max={2}
                                                step={0.05}
                                                onChange={(_, v) => setTtsPitch(Array.isArray(v) ? v[0] : v)}
                                                valueLabelDisplay="auto"
                                            />
                                        </Box>
                                        <Box style={{marginBottom: 10}}>
                                            <Typography variant="caption"
                                                        style={{color: '#666'}}>Volume: {ttsVolume.toFixed(2)}</Typography>
                                            <Slider
                                                size="small"
                                                value={ttsVolume}
                                                min={0}
                                                max={1}
                                                step={0.05}
                                                onChange={(_, v) => setTtsVolume(Array.isArray(v) ? v[0] : v)}
                                                valueLabelDisplay="auto"
                                            />
                                        </Box>

                                        <Box style={{display: 'flex', justifyContent: 'space-between', marginTop: 8}}>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setTtsRate(1);
                                                    setTtsPitch(1);
                                                    setTtsVolume(1);
                                                }}
                                                sx={{
                                                    color: '#ff6468',
                                                    border: '1px solid #ff6468',
                                                    '&:hover': {
                                                        backgroundColor: '#ff6468',
                                                        borderColor: '#ff6468',
                                                        color: '#ffff',
                                                    },
                                                }}
                                            >
                                                Reset defaults
                                            </Button>
                                            <Button size="small"
                                             sx={{
                                                 color: '#ff6468',
                                                 border: '1px solid #ff6468',
                                                 '&:hover': {
                                                     backgroundColor: '#ff6468',
                                                     borderColor: '#ff6468',
                                                     color: '#ffff',
                                                 },
                                            }}
                                            onClick={() => setTtsSettingsAnchor(null)}>
                                                Close
                                            </Button>
                                        </Box>
                                    </Box>
                                </Popover>
                            </div>

                            <Button
                                variant="outlined"
                                style={{
                                    fontSize: {xs: '12px', sm: 'auto',},
                                    border: '1px solid #0097B2',
                                    color: '#0097B2',
                                    backgroundColor: '#fff',
                                    padding: '8px 20px',
                                    borderRadius: '20px',
                                    opacity: currentSegment === segments.length - 1 ? 0.5 : 1,
                                    minWidth: '220px',
                                }}
                                onClick={handleNextSegment}
                                disabled={currentSegment === segments.length - 1}
                            >
                                View Next Segment
                            </Button>
                        </Box>

                        {/* Launch Program Button */}
                        {/*<Box style={{*/}
                        {/*    display: 'flex',*/}
                        {/*    justifyContent: 'center',*/}
                        {/*    width: '100%',*/}
                        {/*    marginBottom: '20px'*/}
                        {/*}}>*/}

                        {/*    <Dialog open={dialogOpen} onClose={handleDialogClose}*/}
                        {/*            PaperProps={{*/}
                        {/*                sx: {*/}
                        {/*                    paddingBottom: '20px',*/}
                        {/*                    borderRadius: '15px',*/}
                        {/*                    overflow: {xs: 'scroll', sm: 'hidden'},*/}
                        {/*                }*/}
                        {/*            }}>*/}
                        {/*        <DialogContent>*/}
                        {/*            <Typography>Are you sure you want to launch this program?</Typography>*/}
                        {/*        </DialogContent>*/}
                        {/*        <DialogActions sx={{justifyContent: 'center'}}>*/}
                        {/*            <Button*/}
                        {/*                onClick={handleDialogClose}*/}
                        {/*                variant="outlined"*/}
                        {/*                style={{*/}
                        {/*                    color: '#FF5757',*/}
                        {/*                    borderColor: '#FF5757',*/}
                        {/*                    fontWeight: 600,*/}
                        {/*                    padding: '5px 35px',*/}
                        {/*                    borderRadius: '20px',*/}
                        {/*                }}*/}
                        {/*            >*/}
                        {/*                No*/}
                        {/*            </Button>*/}
                        {/*            <Button*/}
                        {/*                onClick={handleLaunchProgram}*/}
                        {/*                variant="contained"*/}
                        {/*                style={{*/}
                        {/*                    backgroundColor: '#FF5757',*/}
                        {/*                    color: '#fff',*/}
                        {/*                    fontWeight: 600,*/}
                        {/*                    padding: '5px 35px',*/}
                        {/*                    borderRadius: '20px',*/}
                        {/*                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',*/}
                        {/*                }}*/}
                        {/*            >*/}
                        {/*                Yes*/}
                        {/*            </Button>*/}
                        {/*        </DialogActions>*/}
                        {/*    </Dialog>*/}
                        {/*</Box>*/}
                    </Box>

                    {/* Right column slide navigation arrow buttons */}
                    {(currentSlide > 0 || showPrevSegmentButton) && (
                        <IconButton
                            onClick={handlePrevSlide}
                            disableFocusRipple
                            disableRipple
                            sx={{
                                "&:focus": {outline: "none"},
                                "&:focus-visible": {outline: "none"},
                            }}
                            style={{
                                paddingLeft: '0px',
                                paddingRight: '0px',
                                outline: 'none',
                                position: 'absolute',
                                top: '50%',
                                left: '-8px',
                                transform: 'translateY(-50%)',
                                transition: 'all 0.3s ease',
                                backgroundColor: '#ef6528',
                                borderTopRightRadius: '56px',
                                borderBottomRightRadius: '56px',
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                cursor: 'pointer',
                                width: 56,
                                height: 112,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                boxShadow: '0 8px 18px rgba(239,101,40,0.25)'
                            }}
                        >
                            <ArrowLeft style={{fontSize: 120}}/>
                        </IconButton>
                    )}
                    {((currentSegmentSlides.length > 1 && currentSlide < currentSegmentSlides.length - 1) || showNextSegmentButton) && (
                        <IconButton onClick={handleNextSlide}
                                    disableFocusRipple
                                    disableRipple
                                    sx={{
                                        "&:focus": {outline: "none"},
                                        "&:focus-visible": {outline: "none"},
                                    }}
                                    style={{
                                        paddingRight: '0px',
                                        paddingLeft: '0px',
                                        outline: 'none',
                                        position: 'absolute',
                                        top: '50%',
                                        right: '-7px',
                                        transform: 'translateY(-50%)',
                                        transition: 'all 0.3s ease',
                                        backgroundColor: '#ef6528',
                                        borderTopLeftRadius: '56px',
                                        borderBottomLeftRadius: '56px',
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        cursor: 'pointer',
                                        width: 56,
                                        height: 112,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        boxShadow: '0 8px 18px rgba(239,101,40,0.25)'
                                    }}
                        >
                            <ArrowRight style={{fontSize: 120}}/>
                        </IconButton>
                    )}

                </Box>
            ) : (
                <Box className={classes.rightContainer}
                     style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <Typography variant="h5" style={{color: '#777', textAlign: 'center'}}>
                        Select a session from the menu to view content
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default DQProProgPreview;
